interface CaptchaChallenge {
    provider: string;
    siteKey: string;
    mode: 'visible' | 'invisible';
    decisionId: string;
    scriptUrl: string;
    ttlSeconds: number | null;
}

interface CaptchaFailure {
    decisionId: string;
    code: string;
    errorCodes?: string[];
}

interface CaptchaSdk {
    CAPTCHA_REPLAY_HEADER: string;
    CAPTCHA_TOKEN_HEADER: string;
    CAPTCHA_DECISION_ID_HEADER: string;
    CAPTCHA_VERIFY_FAILED_HEADER: string;
    CaptchaError: new (message: string, code: string, errorCodes?: string[]) => Error;
    captchaController: {
        options: { scriptHostAllowlist?: string[] };
        solveChallenge(challenge: CaptchaChallenge): PromiseLike<{ token: string; decisionId: string }>;
        notifyVerified(detail: { decisionId: string }): void;
        notifyFailed(detail: CaptchaFailure): void;
    };
    parseCaptchaChallenge(
        status: number,
        headers: ng.IHttpHeadersGetter,
        body: Object,
        options: { scriptHostAllowlist?: string[] }
    ): CaptchaChallenge | null;
}

interface CaptchaWindow extends ng.IWindowService {
    ExlCaptchaReady?: PromiseLike<CaptchaSdk>;
}

interface CaptchaResponseBody {
    message?: string;
    errorCodes?: string[];
}

// Needed before the SDK is loaded (the SDK exports the same values but is loaded lazily).
const CAPTCHA_REQUIRED_STATUS = 428;
const CAPTCHA_REQUIRED_HEADER = 'X-Captcha-Required';
const CAPTCHA_LOADER_URL = '/infra/captcha/exl-captcha-loader.js';
const VERIFY_REJECTED_CODE = 'verify_rejected';

export function hcaptchaInterceptor(
    $injector: ng.auto.IInjectorService,
    $q: ng.IQService,
    $window: CaptchaWindow,
    $document: ng.IDocumentService,
    $log: ng.ILogService
): ng.IHttpInterceptor {
    let sdkPromise: ng.IPromise<CaptchaSdk> | null;
    let loaderScript: HTMLScriptElement | null = null;

    function toAngularPromise<T>(promise: PromiseLike<T>): ng.IPromise<T> {
        return $q<T>((resolve, reject) => promise.then(resolve, reject));
    }

    function loadSdk(): ng.IPromise<CaptchaSdk> {
        if (!sdkPromise) {
            if ($window.ExlCaptchaReady) {
                sdkPromise = toAngularPromise($window.ExlCaptchaReady);
            } else {
                const deferred = $q.defer<CaptchaSdk>();
                const script = $document[0].createElement('script');
                loaderScript = script;
                script.src = CAPTCHA_LOADER_URL;
                script.async = true;
                script.onload = () => {
                    if (!$window.ExlCaptchaReady) {
                        deferred.reject(new Error('The hCaptcha SDK loader did not initialize.'));
                        return;
                    }
                    $window.ExlCaptchaReady.then(deferred.resolve, deferred.reject);
                };
                script.onerror = () => deferred.reject(new Error('Could not load the hCaptcha SDK.'));
                sdkPromise = deferred.promise;
                $document[0].head.appendChild(script);
            }
            sdkPromise = sdkPromise.catch(error => {
                $log.error('Failed to load the hCaptcha SDK.', error);
                // Do not cache the failure: allow the next challenge to retry loading the SDK.
                sdkPromise = null;
                delete $window.ExlCaptchaReady;
                if (loaderScript && loaderScript.parentNode) {
                    loaderScript.parentNode.removeChild(loaderScript);
                }
                loaderScript = null;
                return $q.reject(error);
            });
        }
        return sdkPromise as ng.IPromise<CaptchaSdk>;
    }

    return {
        responseError: (response: ng.IHttpPromiseCallbackArg<CaptchaResponseBody>) => {
            if (!response || response.status !== CAPTCHA_REQUIRED_STATUS || !response.config ||
                String(response.headers(CAPTCHA_REQUIRED_HEADER)).toLowerCase() !== 'true') {
                return $q.reject(response);
            }

            const headers = response.config.headers || {};

            return loadSdk().then(sdk => {
                const controller = sdk.captchaController;
                // This request is already the single allowed replay.
                const replayHeader = sdk.CAPTCHA_REPLAY_HEADER.toLowerCase();
                if (Object.keys(headers).some(name => name.toLowerCase() === replayHeader)) {
                    return $q.reject(response);
                }
                const challenge = sdk.parseCaptchaChallenge(
                    response.status, response.headers, response.data, controller.options);
                if (!challenge) {
                    return $q.reject(response);
                }

                return toAngularPromise(controller.solveChallenge(challenge)).then(solved => {
                    const replay: ng.IRequestConfig = {
                        ...response.config,
                        headers: {
                            ...headers,
                            [sdk.CAPTCHA_REPLAY_HEADER]: '1',
                            [sdk.CAPTCHA_TOKEN_HEADER]: solved.token,
                            [sdk.CAPTCHA_DECISION_ID_HEADER]: solved.decisionId
                        },
                        withCredentials: true
                    };
                    // Resolve $http lazily to avoid a circular dependency while its chain is built.
                    const $http = $injector.get<ng.IHttpService>('$http');
                    return $http(replay).then(result => {
                        controller.notifyVerified({ decisionId: solved.decisionId });
                        return result;
                    }, (error: ng.IHttpPromiseCallbackArg<CaptchaResponseBody>) => {
                        if (error.status !== 403 ||
                            String(error.headers(sdk.CAPTCHA_VERIFY_FAILED_HEADER)).toLowerCase() !== 'true') {
                            return $q.reject(error);
                        }
                        const body = error.data || {};
                        const captchaError = new sdk.CaptchaError(
                            body.message || 'Captcha verification was rejected.',
                            VERIFY_REJECTED_CODE, body.errorCodes);
                        controller.notifyFailed({
                            decisionId: solved.decisionId,
                            code: VERIFY_REJECTED_CODE,
                            errorCodes: body.errorCodes
                        });
                        return $q.reject(captchaError);
                    });
                });
            }, () => {
                // SDK-load failure is passthrough; the original server response remains authoritative.
                return $q.reject(response);
            });
        }
    };
}

hcaptchaInterceptor.$inject = ['$injector', '$q', '$window', '$document', '$log'];



// WEBPACK FOOTER //
// ./src/main/webapp/components/security/captcha/hcaptcha.interceptor.ts